import type { DashboardChart, TrendDirection } from "./types";

export function trendTone(direction: TrendDirection): string {
  if (direction === "up") {
    return "is-up";
  }

  if (direction === "down") {
    return "is-down";
  }

  return "is-flat";
}

export function formatDataUrl(path: string): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  return `${base}${path}`;
}

/**
 * Pulls a compact list of numeric values out of a chart's first series so the KPI
 * strip can render a small trend sparkline without the pipeline needing to emit a
 * dedicated summary series. Returns an empty array when no usable numeric data exists.
 */
export function extractSparklineValues(chart: DashboardChart | undefined, maxPoints = 24): number[] {
  if (!chart) {
    return [];
  }

  const series = chart.option.series;
  const firstSeries = Array.isArray(series) ? series[0] : series;

  if (typeof firstSeries !== "object" || firstSeries === null) {
    return [];
  }

  const data = (firstSeries as Record<string, unknown>).data;

  if (!Array.isArray(data)) {
    return [];
  }

  const values = data
    .map((point) => (typeof point === "number" ? point : isPointRecord(point) ? point.value : null))
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));

  return values.slice(-maxPoints);
}

function isPointRecord(value: unknown): value is { value: unknown } {
  return typeof value === "object" && value !== null && "value" in value;
}

export function buildSparklinePath(values: number[], width = 100, height = 30, padding = 3): string {
  if (values.length < 2) {
    return "";
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const stepX = (width - padding * 2) / (values.length - 1);

  return values
    .map((value, index) => {
      const x = padding + index * stepX;
      const y = padding + (1 - (value - min) / span) * (height - padding * 2);
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");
}
